import * as React from "react";

/**
 * Tracks a container element's client width, reflowing on resize.
 *
 * Uses the element's OWN window's `ResizeObserver` (via `ownerDocument.defaultView`), not the ambient
 * one. The Canvas Debugger runs in a pop-out window that shares the parent's JS context, and the parent
 * window's `ResizeObserver` does not reliably fire for elements living in the pop-out's document — so
 * SVG-based views (flame graph, timeline) would never re-measure when their pane is resized.
 */
export function useContainerWidth<T extends HTMLElement>(ref: React.RefObject<T | null>, initial = 600, min = 0): number {
    const [width, setWidth] = React.useState(initial);

    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const update = () => setWidth(Math.max(min, el.clientWidth));
        update();

        const win = el.ownerDocument?.defaultView ?? window;
        const RO = win.ResizeObserver ?? window.ResizeObserver;
        const ro = new RO(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref, min]);

    return width;
}
